/**
 * Sim 9 on the TV (Cole, 2026-09-25: "Phone should contain the button clicks.
 * TV should contain the details."). While a TV is connected the phone strips
 * its own Sim 9 HUD, so every detail it used to draw (course, HOLE n/9, PAR,
 * THIS HOLE, ROUND, the required distance, whose turn, the scorecard) is drawn
 * here from what the phone publishes. Pure so `node --test` covers it.
 *
 * Phone sources: Sim9PlayScreen (solo and friend challenge),
 * Sim9StrokePlayScreen (local hotseat), Sim9MatchPlayScreen,
 * Sim9MatchResponseScreen, and Sim9Scoreboard (the scorecard notation).
 */
import { GIMME_FT, SIM9_COURSES, parForDistance, type Sim9Course } from '../../../packages/putt-scene/src/courses';
import type { TvGame, TvHudResult, TvHudTone, TvPuttEvent, TvTransition } from './protocol';
import { ownPutt, puttMade } from './hudModel';
import { formatDistance } from './units';

export type Sim9Game = Extract<TvGame, { kind: 'sim9' }>;

/** A running to-par score as the phone writes it: "E", "+2", "-1". */
const TO_PAR = /^(E|[+-]\d+)$/;

export function isToParScore(value: string | null | undefined): boolean {
  return typeof value === 'string' && TO_PAR.test(value.trim());
}

export function toParLabel(toPar: number): string {
  if (!Number.isFinite(toPar) || toPar === 0) return 'E';
  return toPar > 0 ? `+${toPar}` : `${toPar}`;
}

/** Under par gold-green, over par red, like the phone's circles and squares. */
export function toParTone(value: string | null | undefined): TvHudTone {
  const text = value?.trim();
  if (!text || !isToParScore(text) || text === 'E') return 'neutral';
  return text.startsWith('-') ? 'success' : 'danger';
}

export interface Sim9Standing {
  name: string;
  color: string | null;
  /** To-par label, "E" / "+2", or a stroke total when to-par is unknown. */
  score: string;
  tone: TvHudTone;
  thisHole: number | null;
  active: boolean;
  /** Holed out on this hole (waiting for the others). */
  done: boolean;
}

export interface Sim9HudBox {
  label: string;
  value: string;
  tone: TvHudTone;
  /** A sentence ("ALL SQUARE", "COLE LEADS") rather than a number. */
  long: boolean;
}

export interface Sim9HudModel {
  courseName: string;
  holeLine: string;
  requiredText: string;
  /** THIS HOLE, then the round or match box(es). */
  boxes: Sim9HudBox[];
  player: { name: string; color: string } | null;
  standings: Sim9Standing[];
}

/**
 * The HUD for a `game.kind: 'sim9'` payload. What rides in `status` differs
 * by screen, and the second box follows the phone:
 *
 * - Sim9PlayScreen (solo, challenger): `status` = `toParText`, label ROUND.
 * - Sim9MatchResponseScreen: `status` = "ALL SQUARE" / "2 UP" / "1 DOWN" or
 *   the locked scoreline, label "VS @handle" (the phone's MATCH box).
 * - Sim9StrokePlayScreen: `status` = `liveStatusTitle` ("COLE LEADS",
 *   "COLE 2 UP", "ALL SQUARE", "SCORECARD") or "SUDDEN DEATH · HOLE n",
 *   label ROUND. The phone's box there is the leader's, so the TV titles it
 *   STANDINGS; the active player's own round to par is `roundToPar` (a
 *   publishing gap), drawn as a third box when it arrives.
 */
export function sim9HudModel(game: Sim9Game, fallbackColor?: string | null): Sim9HudModel {
  const status = typeof game.status === 'string' ? game.status.trim() : '';
  const label = (game.scoreLabel || 'ROUND').trim().toUpperCase();
  const statusIsScore = isToParScore(status);
  const boxes: Sim9HudBox[] = [
    { label: 'THIS HOLE', value: Number.isFinite(game.strokes) ? String(game.strokes) : '—', tone: 'neutral', long: false },
  ];
  if (statusIsScore) {
    boxes.push({ label, value: status, tone: toParTone(status), long: false });
  } else {
    if (status) {
      boxes.push({
        label: label === 'ROUND' ? 'STANDINGS' : label,
        value: status.toUpperCase(),
        // "2 UP" / "1 DOWN" are the responder's own match (the VS @handle
        // box); a stroke-play sentence ("COLE 1 UP") names whoever leads, so
        // it takes no win/lose color.
        tone: label === 'ROUND'
          ? 'gold'
          : /\bDOWN\b/.test(status.toUpperCase()) ? 'danger' : /\bUP\b/.test(status.toUpperCase()) ? 'success' : 'gold',
        long: status.length > 4,
      });
    }
    const own = typeof game.roundToPar === 'string' ? game.roundToPar.trim() : '';
    if (own) boxes.push({ label: 'ROUND', value: own, tone: toParTone(own), long: false });
  }
  const name = game.playerName?.trim();
  const color = game.playerColor?.trim() || fallbackColor?.trim() || null;
  const required = Number(game.requiredFt);
  return {
    courseName: (game.courseName ?? '').toUpperCase(),
    holeLine: `HOLE ${game.hole}/${game.holes || 9} · PAR ${game.par}`,
    requiredText: Number.isFinite(required)
      ? formatDistance(required, required % 1 === 0 ? 0 : 1)
      : '—',
    boxes,
    player: name ? { name, color: color ?? '#c8922a' } : null,
    standings: sim9Standings(game),
  };
}

/**
 * The result line after a Sim 9 putt settles: a make, or a gimme (a leave
 * inside GIMME_FT finishes the hole with a tap-in stroke,
 * `sim9HoleCompletionStrokes`). Other misses need no line: the strip shows
 * the leave and the distance box shows the next putt. Null for a ghost.
 */
export function sim9PuttResult(
  event: TvPuttEvent | null | undefined,
  gimmeFt: number | null | undefined = GIMME_FT,
): TvHudResult | null {
  const own = ownPutt(event);
  if (!own) return null;
  if (puttMade(own)) return { label: 'IN THE HOLE', tone: 'success' };
  const leave = own.proximityFt;
  const radius = gimmeFt != null && Number.isFinite(gimmeFt) && gimmeFt > 0 ? gimmeFt : GIMME_FT;
  if (leave != null && Number.isFinite(leave) && Math.abs(leave) <= radius) {
    return { label: 'GIMME', detail: 'TAP-IN · HOLE COMPLETE', tone: 'gold' };
  }
  return null;
}

function sim9Standings(game: Sim9Game): Sim9Standing[] {
  if (!Array.isArray(game.players)) return [];
  return game.players
    .filter((player) => player && typeof player.name === 'string' && player.name.trim())
    .map((player) => {
      const toPar = typeof player.toPar === 'number' && Number.isFinite(player.toPar) ? toParLabel(player.toPar) : null;
      const total = typeof player.total === 'number' && Number.isFinite(player.total) ? String(player.total) : null;
      const score = toPar ?? total ?? '—';
      return {
        name: player.name.trim(),
        color: player.color?.trim() || null,
        score,
        tone: toPar ? toParTone(toPar) : 'neutral',
        thisHole: typeof player.strokes === 'number' && Number.isFinite(player.strokes) ? player.strokes : null,
        active: player.active === true,
        done: player.holed === true,
      };
    });
}

/* ---- The scorecard (hole-done, scorecard-open, round-done transitions) ---- */

/** The phone's Sim9Scoreboard notation: circle = birdie, double circle =
 * eagle or better, nothing = par, square = bogey, double square = double
 * bogey or worse. */
export type ScoreMark = 'eagle' | 'birdie' | 'par' | 'bogey' | 'double';

export function scoreMark(strokes: number | null | undefined, par: number | null | undefined): ScoreMark | null {
  if (strokes == null || par == null || !Number.isFinite(strokes) || !Number.isFinite(par)) return null;
  const diff = strokes - par;
  if (diff <= -2) return 'eagle';
  if (diff === -1) return 'birdie';
  if (diff === 0) return 'par';
  if (diff === 1) return 'bogey';
  return 'double';
}

export interface Sim9ScorecardCell {
  text: string;
  mark: ScoreMark | null;
}

export interface Sim9ScorecardRow {
  label: string;
  accent: string | null;
  cells: Sim9ScorecardCell[];
  total: string;
  /** Total to par over the holes played, when every played hole has a par. */
  toPar: string | null;
}

export interface Sim9ScorecardModel {
  courseName: string | null;
  holes: Array<{ number: number; distanceFt: number | null; par: number | null }>;
  parTotal: number | null;
  rows: Sim9ScorecardRow[];
}

const SOLO_STAT = /^HOLE\s+(\d+)\s*·\s*PAR\s+(\d+)$/i;

export function sim9CourseFor(hint: { courseId?: string | null; names?: Array<string | null | undefined> }): Sim9Course | null {
  if (hint.courseId) {
    const byId = SIM9_COURSES.find((course) => course.id === hint.courseId);
    if (byId) return byId;
  }
  for (const raw of hint.names ?? []) {
    const text = raw?.trim().toLowerCase();
    if (!text) continue;
    const match = SIM9_COURSES.find((course) => text === course.name.toLowerCase() || text.startsWith(`${course.name.toLowerCase()} ·`));
    if (match) return match;
  }
  return null;
}

function parsedStrokes(value: string | null | undefined): number | null {
  const text = value?.trim();
  if (!text || !/^\d+$/.test(text)) return null;
  return Number.parseInt(text, 10);
}

/**
 * A Sim 9 transition as a real scorecard, or null when it is not one. Two
 * shapes reach the TV today:
 *
 * - Solo (Sim9PlayScreen `studioTransition`): nine `stats`, labeled
 *   "HOLE n · PAR p", valued with the strokes or "—".
 * - Stroke play (Sim9StrokePlayScreen): a `table` with columns "1".."9",
 *   "TOTAL" and one row per player.
 *
 * The hole distances come from the course catalog the phone and page share
 * (the scene's `courseId`, else the course name in the card).
 */
export function sim9ScorecardFromTransition(
  transition: TvTransition | null | undefined,
  courseHint: { courseId?: string | null; courseName?: string | null } = {},
): Sim9ScorecardModel | null {
  if (!transition || !/^SIM 9/i.test(transition.modeTitle ?? '')) return null;
  const course = sim9CourseFor({
    courseId: courseHint.courseId,
    names: [courseHint.courseName, transition.title, transition.detail],
  });

  const stats = transition.stats ?? [];
  const parsedStats = stats.map((stat) => SOLO_STAT.exec(stat.label?.trim() ?? ''));
  if (stats.length > 0 && parsedStats.every((match) => match !== null)) {
    const holes = parsedStats.map((match, index) => {
      const number = Number.parseInt(match![1], 10);
      return {
        number,
        distanceFt: course?.holes[number - 1] ?? null,
        par: Number.parseInt(match![2], 10),
      };
    });
    const strokes = stats.map((stat) => parsedStrokes(stat.value));
    return {
      courseName: course?.name ?? null,
      holes,
      parTotal: holes.reduce((sum, hole) => sum + (hole.par ?? 0), 0),
      rows: [scorecardRow('SCORE', null, strokes, holes.map((hole) => hole.par))],
    };
  }

  const table = transition.table;
  if (table && table.columns.length > 0) {
    const holeColumns = table.columns
      .map((column, index) => ({ column: column.trim(), index }))
      .filter(({ column }) => /^\d+$/.test(column));
    if (holeColumns.length === 0) return null;
    const holes = holeColumns.map(({ column }) => {
      const number = Number.parseInt(column, 10);
      const distanceFt = course?.holes[number - 1] ?? null;
      return { number, distanceFt, par: distanceFt == null ? null : parForDistance(distanceFt) };
    });
    const pars = holes.map((hole) => hole.par);
    return {
      courseName: course?.name ?? null,
      holes,
      parTotal: pars.every((par) => par != null) ? pars.reduce<number>((sum, par) => sum + (par ?? 0), 0) : null,
      rows: table.rows.map((row) => scorecardRow(
        row.label,
        row.accent ?? null,
        holeColumns.map(({ index }) => parsedStrokes(row.values[index])),
        pars,
      )),
    };
  }
  return null;
}

function scorecardRow(
  label: string,
  accent: string | null,
  strokes: Array<number | null>,
  pars: Array<number | null>,
): Sim9ScorecardRow {
  let total = 0;
  let parPlayed = 0;
  let parKnown = true;
  let played = 0;
  const cells = strokes.map((value, index) => {
    if (value != null) {
      played += 1;
      total += value;
      const par = pars[index];
      if (par == null) parKnown = false;
      else parPlayed += par;
    }
    return { text: value == null ? '—' : String(value), mark: scoreMark(value, pars[index]) };
  });
  return {
    label,
    accent,
    cells,
    total: played ? String(total) : '—',
    toPar: played && parKnown ? toParLabel(total - parPlayed) : null,
  };
}
